import { groupBy } from 'lodash';
import { CdsExportNotificationError } from 'store/declarations/uk/cds-declaration';
import resolveErrorField from '../resolveErrorField';
import { ErrorCard, ErrorCardContext, ErrorCardField } from '../error-cards/types';
import labelErrorField from '../error-cards/labelErrorField';
import transformCdsErrorField from './transformCdsErrorField';

/**
 * CDS errors come in groups: one error and the relation messages the backend joined to it, which only
 * explain it. Each group is one card - its first message is the error, the others its related fields -
 * so the number on a card counts what the user has to fix.
 */
const getCdsErrorCards = (
    errors: CdsExportNotificationError[] | undefined,
    context: ErrorCardContext
): ErrorCard<CdsExportNotificationError>[] => {
    // A CDS field counts from one in its own notation, and from zero once it is the form's path.
    const toFields = (error: CdsExportNotificationError): ErrorCardField[] => {
        const path = transformCdsErrorField(error.field);
        if (!path) return [];

        const resolved = resolveErrorField(path, { ...context.fieldContext, firstNumber: 0 });
        return [{ ...resolved, label: labelErrorField(path, resolved, 0, context) }];
    };

    const groups = Object.entries(groupBy(errors ?? [], 'groupId'))
        .sort(([groupA], [groupB]) => Number(groupA) - Number(groupB))
        .map(([, groupErrors]) => groupErrors);

    return groups.map(([error, ...relationMessages]) => ({
        code: error.code,
        message: error.message,
        fields: toFields(error),
        related: relationMessages.flatMap(toFields),
        error,
    }));
};

export default getCdsErrorCards;
